// Monthly Statements — the B3 statement queue page (SP-2 WS-2/3) at
// /portfolio-admin/statements. Two queues over the shared LoanReport
// statement lifecycle: "Waiting for your review" (worker- or staff-generated
// bills awaiting the B3 gate before they go to the TPS) and "Returned by the
// servicer" (bills the TPS sent back with a note — fix the data, regenerate).
// Every section states its next action in plain words; no raw enum strings
// (STATEMENT_STATUS_LABEL). Writes gate on Statements.Generate (B3 + ADMIN).

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  EmptyState,
  GlossaryText,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "@/design-system/components/ui";
import { FirstRunTour } from "@/components/ux";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency, formatDateInTz } from "@/design-system/lib/format";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import {
  STATEMENT_STATUS_LABEL,
  type LoanStatementDto,
} from "@/api/portfolioAdmin/loanStatementsApi";
import {
  usePendingReviewStatements,
  useReturnedStatements,
  useRegenerateStatementFromQueue,
} from "@/hooks/portfolioAdmin/useLoanStatements";
import { StatementReviewModal } from "@/components/portfolioAdmin/loanDetail/monthlyCycle/StatementReviewModal";

const VIEW_PERMISSION = "Statements.Generate";

// First-run tour copy (SP-2b) — a one-time orientation banner.
const TOUR_STEPS = [
  {
    title: "Your review queue",
    body: "Statements land here for your review before the servicer sees them.",
  },
  {
    title: "Send to servicer",
    body: "Send to servicer moves a bill to their approval queue.",
  },
  {
    title: "Returned bills",
    body: "Bills the servicer returns for correction show below — regenerate after fixing.",
  },
  {
    title: "See a bill in context",
    body: "Open the loan's Monthly Cycle tab to see any bill in context.",
  },
];

/** "2026-05-01T00:00:00Z" (or "2026-05-01") → "May 2026" — the billing month. */
function billingMonth(periodStart: string): string {
  const d = new Date(`${periodStart.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return periodStart;
  return d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

function LoanLink({ statement: s }: { statement: LoanStatementDto }) {
  return (
    <Link
      to={`/portfolio-admin/loans/${s.loanId}?tab=monthly-cycle`}
      className="text-gold-shadow hover:underline"
    >
      {s.loanNumber}
    </Link>
  );
}

function SectionError({ text, onRetry }: { text: string; onRetry: () => void }) {
  return (
    <div className="flex items-center gap-space-3" role="alert">
      <p className="text-small text-status-error">{text}</p>
      <Button variant="secondary" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

export default function StatementQueuePage() {
  const { tz } = useDateFormatter();
  const { hasPermission, hasRoleCode } = useAuth();
  const toast = useToast();

  const pending = usePendingReviewStatements();
  const returned = useReturnedStatements();
  const regenerate = useRegenerateStatementFromQueue();

  // The D-9 review screen (approve + adjust + thread + send) opens per bill;
  // the regenerate quick-confirm stays for the returned-queue fast path.
  const [reviewTarget, setReviewTarget] = useState<LoanStatementDto | null>(null);
  const [regenTarget, setRegenTarget] = useState<LoanStatementDto | null>(null);

  const confirmRegenerate = async () => {
    if (!regenTarget) return;
    try {
      await regenerate.mutateAsync({
        statementId: regenTarget.id,
        loanId: regenTarget.loanId,
      });
      toast.success("Regenerated — the new bill is waiting for your review.");
      setRegenTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (!hasPermission(VIEW_PERMISSION) && !hasRoleCode("ADMIN")) {
    return (
      <div className="p-space-5">
        <PageHeader eyebrow="Finance" title="Monthly Statements" />
        <EmptyState
          title="Not authorized"
          description="You don't have permission to review and send monthly statements."
        />
      </div>
    );
  }

  return (
    <div className="p-space-5">
      <PageHeader
        eyebrow="Finance"
        title="Monthly Statements"
        description="Each loan's monthly bill passes through here twice: you review it before it goes to the servicer, and it comes back here if the servicer returns it."
      />

      <FirstRunTour
        tourKey="tour-statement-queue"
        steps={TOUR_STEPS}
        className="mb-space-4"
      />

      <section className="mb-space-6" data-testid="statements-pending-section">
        <h2 className="font-display font-bold text-h4 text-onyx">
          Waiting for your review
        </h2>
        <p className="text-small text-gray-mid mt-space-1 mb-space-3 max-w-reading">
          Statements the system (or you) generated this month. Look each one
          over, then send it to the servicer.
        </p>
        {pending.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : pending.isError ? (
          <SectionError
            text="Couldn't load the statements waiting for review."
            onRetry={() => void pending.refetch()}
          />
        ) : (pending.data ?? []).length === 0 ? (
          <EmptyState
            title="Nothing waiting for review"
            description="Nothing waiting for review. Statements generate automatically on the 1st of each month."
            variant="noResults"
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Period</TH>
                <TH>Servicer</TH>
                <TH className="text-right">Amount due</TH>
                <TH>Prepared</TH>
                <TH>Status</TH>
                <TH>
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {(pending.data ?? []).map((s) => (
                <TR key={s.id} data-testid={`statement-pending-row-${s.id}`}>
                  <TD>
                    <LoanLink statement={s} />
                  </TD>
                  <TD className="whitespace-nowrap">{billingMonth(s.periodStart)}</TD>
                  <TD>{s.tpsName ?? "—"}</TD>
                  <TD className="text-right tabular-nums">
                    {formatCurrency(s.amountDue)}
                  </TD>
                  <TD className="text-gray-mid whitespace-nowrap">
                    {formatDateInTz(s.preparedAt, tz)}
                  </TD>
                  <TD>
                    <StatusBadge tone="warn">
                      {STATEMENT_STATUS_LABEL[s.statementStatus]}
                    </StatusBadge>
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-space-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setReviewTarget(s)}
                        data-testid={`statement-review-${s.id}`}
                      >
                        Review & send
                      </Button>
                      <Link
                        to={`/portfolio-admin/loans/${s.loanId}?tab=monthly-cycle`}
                        className="text-small text-gold-shadow hover:underline self-center"
                      >
                        Open loan
                      </Link>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>

      <section data-testid="statements-returned-section">
        <h2 className="font-display font-bold text-h4 text-onyx">
          Returned by the servicer
        </h2>
        <p className="text-small text-gray-mid mt-space-1 mb-space-3 max-w-reading">
          Bills the servicer sent back with a note. Fix the underlying data,
          then regenerate.
        </p>
        {returned.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : returned.isError ? (
          <SectionError
            text="Couldn't load the returned bills."
            onRetry={() => void returned.refetch()}
          />
        ) : (returned.data ?? []).length === 0 ? (
          <EmptyState
            title="No returned bills"
            description="No returned bills. When a servicer returns one, it lands here with their note."
            variant="noResults"
          />
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Loan</TH>
                <TH>Period</TH>
                <TH className="text-right">Amount</TH>
                <TH>Why it was returned</TH>
                <TH>Status</TH>
                <TH>
                  <span className="sr-only">Actions</span>
                </TH>
              </TR>
            </THead>
            <TBody>
              {(returned.data ?? []).map((s) => (
                <TR key={s.id} data-testid={`statement-returned-row-${s.id}`}>
                  <TD>
                    <LoanLink statement={s} />
                  </TD>
                  <TD className="whitespace-nowrap">{billingMonth(s.periodStart)}</TD>
                  <TD className="text-right tabular-nums">
                    {formatCurrency(s.amountDue)}
                  </TD>
                  <TD className="max-w-reading">
                    <span className="text-onyx whitespace-normal">
                      {s.returnedReason ? (
                        <GlossaryText text={s.returnedReason} />
                      ) : (
                        "—"
                      )}
                    </span>
                  </TD>
                  <TD>
                    <StatusBadge tone="danger">
                      {STATEMENT_STATUS_LABEL[s.statementStatus]}
                    </StatusBadge>
                  </TD>
                  <TD>
                    <div className="flex justify-end gap-space-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => setRegenTarget(s)}
                        disabled={regenerate.isPending}
                        data-testid={`statement-regenerate-${s.id}`}
                      >
                        Regenerate
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setReviewTarget(s)}
                        data-testid={`statement-review-${s.id}`}
                      >
                        Review & reply
                      </Button>
                      <Link
                        to={`/portfolio-admin/loans/${s.loanId}?tab=monthly-cycle`}
                        className="text-small text-gold-shadow hover:underline self-center"
                      >
                        Open loan
                      </Link>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </section>

      {reviewTarget && (
        <StatementReviewModal
          loanId={reviewTarget.loanId}
          statementId={reviewTarget.id}
          period={reviewTarget.periodStart.slice(0, 7)}
          tpsName={reviewTarget.tpsName}
          open
          onClose={() => setReviewTarget(null)}
        />
      )}

      <Modal
        open={regenTarget !== null}
        onClose={() => setRegenTarget(null)}
        title="Regenerate this bill"
        description={
          regenTarget
            ? `This replaces the returned bill with a fresh one built from current data — fix fees or loan terms first if the reason calls for it. The new bill comes back to you for review.`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setRegenTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void confirmRegenerate()}
              loading={regenerate.isPending}
              data-testid="statement-regenerate-confirm"
            >
              Regenerate bill
            </Button>
          </>
        }
      >
        {regenTarget?.returnedReason && (
          <p
            className="text-small text-onyx"
            data-testid="statement-regenerate-reason"
          >
            <span className="text-caption uppercase tracking-label text-gray-mid block">
              The servicer's note
            </span>
            <GlossaryText text={regenTarget.returnedReason} />
          </p>
        )}
      </Modal>
    </div>
  );
}
